import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Building2, Users, ArrowRight, CheckCircle, ChevronDown, ChevronUp } from 'lucide-react';
import { Navbar } from '../components/Navbar';
import { Footer } from '../components/Footer';
import { PageSEO } from '../components/SEO/PageSEO';
import Mixpanel from '../utils/mixpanel';

const missionPageFaqs = [
  {
    question: "What makes LetsHost different from venue directories?",
    answer: "Unlike DIY platforms where you search and compare venues yourself, we provide a dedicated event concierge who curates matches specifically for your event. We know our venue partners intimately—their spaces, pricing, and which events work best. Our focus is on groups of 25+ people looking for premium private dining and corporate event venues."
  },
  {
    question: "Who handles my event planning?",
    answer: "A real human concierge handles your entire booking process. They're available to answer questions, guide you through comparing options, and help with any issues that arise."
  },
  {
    question: "What types of events does LetsHost specialize in?",
    answer: "We focus on corporate events, private dining rooms, cocktail parties, engagement celebrations, rehearsal dinners, and full restaurant buyouts for groups of 25 to 250+ guests."
  }
];

const FAQItem = ({ faq, isOpen, toggle }) => (
  <div className="border-b border-stone-200 py-4">
    <button
      onClick={toggle}
      className="w-full flex justify-between items-center text-left focus:outline-none"
    >
      <span className="text-lg font-semibold text-gray-900 leading-tight pr-4" style={{ fontFamily: "'Playfair Display', serif" }}>
        {faq.question}
      </span>
      {isOpen ? (
        <ChevronUp className="w-5 h-5 text-stone-400 flex-shrink-0" />
      ) : (
        <ChevronDown className="w-5 h-5 text-stone-400 flex-shrink-0" />
      )}
    </button>
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ height: 0, opacity: 0 }}
          animate={{ height: "auto", opacity: 1 }}
          exit={{ height: 0, opacity: 0 }}
          transition={{ duration: 0.3 }}
          className="overflow-hidden"
        >
          <p className="mt-3 text-stone-600 leading-relaxed text-sm">
            {faq.answer}
          </p>
        </motion.div>
      )}
    </AnimatePresence>
  </div>
);

const conciergeFeatures = [
  'Personalized venue recommendations',
  'Managed venue communication and scheduling',
  'Expert guidance throughout your planning process',
  'Knowledge of each venue\'s packages and pricing',
];

const venueFeatures = [
  'Pre-qualified leads matched to their space',
  'Exposure to new clients in their target demographics',
  'Event details shared upfront for better planning',
];

// FAQ Schema for Mission page
const missionPageFaqSchema = [
  {
    question: "What makes LetsHost different from venue directories like Tagvenue or PartySlate?",
    answer: "Unlike directories where you search and compare venues yourself, we provide a dedicated event concierge who curates matches specifically for your event. We know our venue partners intimately—their spaces, pricing, and which events work best. Our focus is on groups of 20+ people looking for premium private dining and corporate event venues, not one-off party bookings."
  },
  {
    question: "Who handles my event planning after I submit my request?",
    answer: "A real human concierge handles your entire booking process. They know NYC venues personally, can answer questions about specific spaces, and guide you through comparing options. You're not left clicking through listings hoping for a response."
  },
  {
    question: "What types of events does LetsHost specialize in?",
    answer: "We focus on corporate events, private dining rooms, cocktail parties, engagement celebrations, rehearsal dinners, and full restaurant buyouts for groups of 20 or more. Our venue network centers on Manhattan with select Brooklyn options."
  },
  {
    question: "How does LetsHost make money?",
    answer: "We charge a small booking fee for our matching and concierge service. Venues pay a referral fee when bookings are confirmed. This dual model lets us provide personalized service without requiring massive minimums from hosts."
  },
  {
    question: "Why did you build LetsHost?",
    answer: "We built LetsHost because event planning shouldn't feel like work. Traditional venues require extensive research, cold outreach, and weeks of back-and-forth. We wanted to provide an affordable alternative to full-service event planners for people who just need a venue—not a complete production."
  }
];

export default function Mission() {
  const [openFaqIndex, setOpenFaqIndex] = useState(null);
  useEffect(() => {
    Mixpanel.track('Mission Page View', {
      timestamp: new Date().toISOString(),
    });
  }, []);

  return (
    <div className="min-h-screen flex flex-col bg-white">
      <PageSEO
        title="About LetsHost NYC | Our Mission & Venue Concierge Approach"
        description="Learn how LetsHost's human concierge service differs from venue directories. We match corporate events and private dining parties with Manhattan's best venues. Built by event industry experts."
        path="/mission"
        faqSchema={missionPageFaqSchema}
      />

      <Navbar />

      {/* ───────── HERO ───────── */}
      <section className="pb-6 sm:pb-12 md:pb-16 lg:pb-20 px-5 sm:px-10 lg:px-16" style={{ backgroundColor: '#F5F0EB' }}>
        <div className="max-w-7xl mx-auto mt-2 sm:mt-6 md:mt-8 lg:mt-10">
          <div className="flex flex-col lg:flex-row items-center lg:items-start gap-10 lg:gap-16">

            {/* Left — headline + CTAs */}
            <motion.div
              className="w-full lg:w-1/2 flex-shrink-0"
              initial={{ opacity: 0, y: 28 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.15 }}
            >
              <span className="text-xs tracking-[0.16em] uppercase text-stone-400 font-sans font-medium block mb-5">
                Our Mission
              </span>
              <h1
                className="text-[2.75rem] sm:text-[3.5rem] md:text-[4.2rem] lg:text-[3.6rem] xl:text-[4.4rem] font-bold text-gray-900 leading-[1.06] mb-6 max-w-xs sm:max-w-lg"
                style={{ fontFamily: "'Playfair Display', serif", letterSpacing: '-0.02em' }}
              >
                Planning your event shouldn't feel like work.
              </h1>
              <p className="text-base sm:text-lg text-stone-500 leading-relaxed mb-9 max-w-sm">
                We find the venue so you can focus on your event — handled end-to-end by a real team that knows the city.
              </p>
              <div className="flex flex-col sm:flex-row gap-3">
                <a
                  href="/"
                  className="inline-flex items-center justify-center px-6 py-3.5 rounded-lg font-medium text-stone-900 bg-white hover:bg-stone-50 transition-colors text-sm shadow-sm border border-stone-200"
                >
                  Find Your Venue
                  <ArrowRight className="ml-2 w-4 h-4" />
                </a>
                <a
                  href="mailto:events@letshost.nyc"
                  className="inline-flex items-center justify-center px-6 py-3.5 rounded-lg font-medium text-stone-600 border border-stone-300 bg-white hover:bg-stone-50 transition-colors text-sm"
                >
                  Contact Us
                </a>
              </div>
            </motion.div>

            {/* Right — value prop cards */}
            <motion.div
              className="w-full lg:w-1/2 relative"
              initial={{ opacity: 0, y: 28 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.3 }}
            >
              {/* Card 1 */}
              <div className="relative z-10 bg-white rounded-xl p-5 sm:p-6 shadow-sm border border-stone-100 mb-4">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0" style={{ backgroundColor: '#F5F0EB' }}>
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" className="text-stone-500"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900 text-sm mb-0.5" style={{ fontFamily: "'Playfair Display', serif" }}>Done in 15 minutes</h3>
                    <p className="text-xs text-stone-500 leading-relaxed">Not hours of back-and-forth with venues.</p>
                  </div>
                </div>
              </div>

              {/* Card 2 — offset right */}
              <div className="relative z-10 bg-white rounded-xl p-5 sm:p-6 shadow-sm border border-stone-100 mb-4 ml-6 sm:ml-10">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0" style={{ backgroundColor: '#F5F0EB' }}>
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" className="text-stone-500"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg>
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900 text-sm mb-0.5" style={{ fontFamily: "'Playfair Display', serif" }}>20 – 200 People</h3>
                    <p className="text-xs text-stone-500 leading-relaxed">Venues that fit your exact headcount.</p>
                  </div>
                </div>
              </div>

              {/* Card 3 */}
              <div className="relative z-10 bg-white rounded-xl p-5 sm:p-6 shadow-sm border border-stone-100">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0" style={{ backgroundColor: '#F5F0EB' }}>
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" className="text-stone-500"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/></svg>
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900 text-sm mb-0.5" style={{ fontFamily: "'Playfair Display', serif" }}>A Human in Your Corner</h3>
                    <p className="text-xs text-stone-500 leading-relaxed">Real experts, not search algorithms.</p>
                  </div>
                </div>
              </div>
            </motion.div>

          </div>
        </div>
      </section>

      {/* ───────── HOW WE WORK ───────── */}
      <section className="py-16 sm:py-20 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-6xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="text-center mb-14"
          >
            <h2
              className="text-3xl sm:text-4xl md:text-5xl font-bold text-gray-900 mb-4"
              style={{ fontFamily: "'Playfair Display', serif" }}
            >
              How It Works
            </h2>
            <p className="text-base sm:text-lg text-stone-500 max-w-lg mx-auto">
              We do the heavy lifting.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 lg:gap-8">
            {/* Concierge card */}
            <motion.div
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.1 }}
              className="bg-[#FAFAF8] rounded-xl p-7 sm:p-8 border border-stone-100"
            >
              <div className="flex items-start gap-4 mb-6">
                <div className="bg-white border border-stone-200 rounded-xl p-3 shadow-sm flex-shrink-0">
                  <Users className="w-6 h-6 text-stone-700 stroke-[1.5]" />
                </div>
                <div>
                  <h3
                    className="text-xl font-semibold text-gray-900 mb-1"
                    style={{ fontFamily: "'Playfair Display', serif" }}
                  >
                    Your Personal Event Concierge
                  </h3>
                  <p className="text-sm text-stone-500 leading-relaxed">
                    A dedicated expert in your corner — with relationships at venues across the city.
                  </p>
                </div>
              </div>

              <p className="text-sm text-stone-600 leading-relaxed mb-5">
                We charge a small booking fee before we begin the matchmaking process. That fee funds a dedicated team of event experts who know every venue in our network — their spaces, packages, and pricing — so you don't have to spend weeks researching.
              </p>

              <ul className="space-y-3">
                {conciergeFeatures.map((f) => (
                  <li key={f} className="flex items-start gap-3">
                    <CheckCircle className="w-4 h-4 text-stone-400 flex-shrink-0 mt-0.5" strokeWidth={1.8} />
                    <span className="text-sm text-stone-600">{f}</span>
                  </li>
                ))}
              </ul>
            </motion.div>

            {/* Venue referral card */}
            <motion.div
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.2 }}
              className="bg-[#FAFAF8] rounded-xl p-7 sm:p-8 border border-stone-100"
            >
              <div className="flex items-start gap-4 mb-6">
                <div className="bg-white border border-stone-200 rounded-xl p-3 shadow-sm flex-shrink-0">
                  <Building2 className="w-6 h-6 text-stone-700 stroke-[1.5]" />
                </div>
                <div>
                  <h3
                    className="text-xl font-semibold text-gray-900 mb-1"
                    style={{ fontFamily: "'Playfair Display', serif" }}
                  >
                    Introducing Venues to Hosts
                  </h3>
                  <p className="text-sm text-stone-500 leading-relaxed">
                    Venues pay a small referral fee when we match them with the right fit.
                  </p>
                </div>
              </div>

              <p className="text-sm text-stone-600 leading-relaxed mb-5">
                We know which events work best at each venue — insight that takes most people weeks to gather. Venues benefit from pre-qualified leads who already know what they want, and hosts benefit from curated introductions instead of cold outreach.
              </p>

              <ul className="space-y-3">
                {venueFeatures.map((f) => (
                  <li key={f} className="flex items-start gap-3">
                    <CheckCircle className="w-4 h-4 text-stone-400 flex-shrink-0 mt-0.5" strokeWidth={1.8} />
                    <span className="text-sm text-stone-600">{f}</span>
                  </li>
                ))}
              </ul>

              <div className="mt-5 pt-5 border-t border-stone-200">
                <p className="text-sm text-stone-500 italic">
                  "We create win-win relationships between venues and hosts."
                </p>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* ───────── HUMAN TOUCH ───────── */}
      <section className="py-16 sm:py-20 px-4 sm:px-6 lg:px-8 overflow-hidden">
        <div className="max-w-6xl mx-auto">
          <div className="flex flex-col lg:flex-row items-stretch gap-8 lg:gap-12">
            {/* Photo side */}
            <motion.div
              className="w-full lg:w-5/12"
              initial={{ opacity: 0, x: -24 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <div className="relative rounded-xl overflow-hidden" style={{ aspectRatio: '3 / 4' }}>
                <img
                  src="/Inspo_Gallery/private_dining_nice_stylish.webp"
                  alt="Elegant private dining setup"
                  className="w-full h-full object-cover"
                  loading="lazy"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent" />
              </div>
            </motion.div>

            {/* Text side */}
            <motion.div
              className="w-full lg:w-7/12 flex flex-col justify-center"
              initial={{ opacity: 0, x: 24 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.15 }}
            >
              <span
                className="text-sm tracking-[0.12em] uppercase text-stone-400 mb-3 font-sans font-medium"
              >
                The Human Touch
              </span>

              <h2
                className="text-3xl sm:text-4xl md:text-5xl font-bold text-gray-900 mb-7 leading-tight"
                style={{ fontFamily: "'Playfair Display', serif" }}
              >
                Real experts,<br />not search algorithms.
              </h2>

              <div className="space-y-5">
                <p className="text-base sm:text-lg text-stone-600 leading-relaxed">
                  Unlike venue aggregators where you're left clicking around endlessly with no response, we match you with venues and a real human who understands your vision — available when you need them.
                </p>
                <p className="text-base sm:text-lg text-stone-600 leading-relaxed">
                  Our team has cultivated relationships with venues all across the city. We know their spaces, packages, pricing structures, and which events work best at each location — knowledge that would take you weeks to gather on your own.
                </p>
              </div>

              <div className="mt-10">
                <a
                  href="/"
                  className="inline-flex items-center gap-2 text-stone-900 font-medium text-base group"
                >
                  <span>Start Your Search</span>
                  <span className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-stone-900 text-white group-hover:bg-stone-700 transition-colors">
                    <ArrowRight className="w-3.5 h-3.5" />
                  </span>
                </a>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* ───────── TEAM / STORY ───────── */}
      <section className="py-16 sm:py-20 px-4 sm:px-6 lg:px-8" style={{ backgroundColor: '#F5F0EB' }}>
        <div className="max-w-5xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="text-center mb-12"
          >
            <h2
              className="text-3xl sm:text-4xl md:text-5xl font-bold text-gray-900 mb-4"
              style={{ fontFamily: "'Playfair Display', serif" }}
            >
              Why We Built This
            </h2>
            <p className="text-base sm:text-lg text-stone-500 max-w-lg mx-auto">
              Event industry experts, technologists, and hosts — who've all been in your shoes.
            </p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.1 }}
            className="bg-white rounded-xl p-8 sm:p-10 md:p-12 shadow-sm border border-stone-100"
          >
            <div className="max-w-2xl mx-auto">
              <p className="text-base sm:text-lg text-stone-700 leading-relaxed mb-5">
                Our team blends event expertise with technology innovation. From intimate milestone celebrations to large-scale productions, we've seen how fragmentation creates friction in the industry.
              </p>
              <p className="text-base sm:text-lg text-stone-700 leading-relaxed mb-5">
                We've also seen how people simply don't plan events that they have the budget to do because the process is broken — endless emails, opaque pricing, no clear path forward. We believe venues should be held to standards you can verify — which is why we encourage researching <a href="https://nycrestaurantgrades.com/" target="_blank" rel="noopener noreferrer" class="text-stone-600 underline hover:text-stone-800 transition-colors">NYC restaurant health inspection grades</a> when evaluating spaces.
              </p>
              <p className="text-base sm:text-lg text-stone-700 leading-relaxed mb-5">
                We built LetsHost because we saw a clear gap in the market: event planners are expensive and out of reach for most. You might not know exactly what you need, but you know what you want to experience.
              </p>
              <p className="text-base sm:text-lg text-stone-700 leading-relaxed mb-5">
                We believe there should be an affordable alternative when you just need a venue — not a full-service event planner.
              </p>
              <p className="text-base sm:text-lg text-stone-700 leading-relaxed">
                Our mission is simple: let you focus on creating memorable moments with your guests — not drowning in venue emails for weeks.
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* ───────── CTA ───────── */}
      <section className="py-16 sm:py-20 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-3xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
          >
            <h2
              className="text-3xl sm:text-4xl md:text-5xl font-bold text-gray-900 mb-5"
              style={{ fontFamily: "'Playfair Display', serif" }}
            >
              Let Us Find Your Venue.
            </h2>
            <p className="text-base sm:text-lg text-stone-500 mb-9 max-w-lg mx-auto">
              Tell us about your event and we'll introduce you to the perfect space.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <a
                href="/"
                className="inline-flex items-center justify-center px-7 py-3.5 rounded-lg font-medium text-white bg-stone-900 hover:bg-stone-700 transition-colors text-base shadow-md"
              >
                Get Started
              </a>
              <a
                href="mailto:events@letshost.nyc"
                className="inline-flex items-center justify-center px-7 py-3.5 rounded-lg font-medium text-stone-700 bg-white border border-stone-300 hover:bg-stone-50 transition-colors text-base"
              >
                Contact Us
              </a>
            </div>
          </motion.div>
        </div>
      </section>

      <Footer />

      {/* ───────── FAQ SECTION ───────── */}
      <section className="py-16 sm:py-24 px-4 sm:px-6 lg:px-8 bg-[#FAFAF8] border-t border-stone-100">
        <div className="max-w-3xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="mb-12 text-center"
          >
            <h2
              className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4"
              style={{ fontFamily: "'Playfair Display', serif" }}
            >
              Mission & Model FAQ
            </h2>
            <p className="text-stone-500">
              Answers to common questions about our concierge approach.
            </p>
          </motion.div>

          <div className="space-y-2">
            {missionPageFaqs.map((faq, idx) => (
              <FAQItem 
                key={idx} 
                faq={faq} 
                isOpen={openFaqIndex === idx} 
                toggle={() => setOpenFaqIndex(openFaqIndex === idx ? null : idx)} 
              />
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
